// src/AdminUsersPage.js
import React, { useState, useEffect } from 'react';
import { db } from './firebase.js';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

function AddUserModal({ isMobile, newUserEmail, setNewUserEmail, newUserPassword, setNewUserPassword, makeAdmin, setMakeAdmin, modalError, modalSuccess, handleAddUser, setShowAddModal, setModalError, setModalSuccess }) {
  const handleSubmit = (e) => {
    e.preventDefault();
    handleAddUser();
  };

  return (
    <div className="modal-overlay">
      <div className={`modal-content ${isMobile ? 'modal-content-mobile' : ''}`}>
        <h2 className="modal-title">Add New User</h2>
        {modalError && <p className="modal-error">{modalError}</p>}
        {modalSuccess && <p className="modal-success">{modalSuccess}</p>}
        <form onSubmit={handleSubmit}>
          <input
            type="email"
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
            placeholder="User email"
            className="modal-input"
            autoFocus
          />
          <input
            type="password"
            value={newUserPassword}
            onChange={(e) => setNewUserPassword(e.target.value)}
            placeholder="Password"
            className="modal-input"
          />
          <label className="modal-checkbox">
            <input
              type="checkbox"
              checked={makeAdmin}
              onChange={(e) => setMakeAdmin(e.target.checked)}
            />
            Make Admin
          </label>
          <div className="modal-button-group">
            <button
              type="submit"
              className="modal-button modal-button-primary"
            >
              Add User
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAddModal(false);
                setNewUserEmail('');
                setNewUserPassword('');
                setMakeAdmin(false);
                setModalError(null);
                setModalSuccess(null);
              }}
              className="modal-button modal-button-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditPreferencesModal({ isMobile, userData, editPreferences, setEditPreferences, modalError, modalSuccess, handleSavePreferences, setShowEditModal, setModalError, setModalSuccess }) {
  const handleSubmit = (e) => {
    e.preventDefault();
    handleSavePreferences();
  };

  return (
    <div className="modal-overlay">
      <div className={`modal-content ${isMobile ? 'modal-content-mobile' : ''}`}>
        <h2 className="modal-title">Edit Preferences for {userData.email}</h2>
        {modalError && <p className="modal-error">{modalError}</p>}
        {modalSuccess && <p className="modal-success">{modalSuccess}</p>}
        <form onSubmit={handleSubmit}>
          <label className="modal-checkbox">
            <input
              type="checkbox"
              checked={editPreferences.emergencyNotifications}
              onChange={(e) => setEditPreferences({ ...editPreferences, emergencyNotifications: e.target.checked })}
            />
            Receive email emergency notifications
          </label>
          <label className="modal-checkbox">
            <input
              type="checkbox"
              checked={editPreferences.updateNotifications}
              onChange={(e) => setEditPreferences({ ...editPreferences, updateNotifications: e.target.checked })}
            />
            Receive updates for message board posts, HOA documents, and calendar events
          </label>
          <div className="modal-button-group">
            <button
              type="submit"
              className="modal-button modal-button-primary"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => {
                setShowEditModal(false);
                setModalError(null);
                setModalSuccess(null);
              }}
              className="modal-button modal-button-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AdminUsersPage({ user, isAdmin, isMobile }) {
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [makeAdmin, setMakeAdmin] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [modalSuccess, setModalSuccess] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editPreferences, setEditPreferences] = useState({
    emergencyNotifications: false,
    updateNotifications: false,
  });
  const navigate = useNavigate();

  // Fetch all users and their preferences
  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }

    const fetchAllUsers = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://us-central1-nantahala-mountain-resor-596d9.cloudfunctions.net/api/listAllUsers', {
          headers: {
            Authorization: `Bearer ${await user.getIdToken()}`,
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}: ${await response.text()}`);
        }
        const data = await response.json();
        // Fetch preferences for each user
        const defaultPrefs = {
          emergencyNotifications: false,
          updateNotifications: false,
        };
        const usersWithPrefs = await Promise.all(
          data.users.map(async (u) => {
            try {
              const prefDoc = await getDoc(doc(db, 'user_preferences', u.uid));
              return {
                ...u,
                preferences: prefDoc.exists() ? prefDoc.data() : defaultPrefs,
              };
            } catch (prefErr) {
              console.warn(`Could not load preferences for ${u.uid}:`, prefErr);
              return { ...u, preferences: defaultPrefs };
            }
          })
        );
        setAllUsers(usersWithPrefs);
        setError(null);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchAllUsers();
  }, [user, isAdmin, navigate]);

  // Handle add or modify admin status
  const handleAdminAction = async (email, isAdmin, action = 'modify') => {
    try {
      const response = await fetch('https://us-central1-nantahala-mountain-resor-596d9.cloudfunctions.net/api/manageAdminClaim', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await user.getIdToken()}`,
        },
        body: JSON.stringify({ email, isAdmin }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}: ${await response.text()}`);
      }
      const result = await response.json();
      setModalSuccess(result.message);
      setModalError(null);
      // Refresh user list
      setAllUsers((prev) =>
        prev.map((u) => (u.email === email ? { ...u, isAdmin } : u))
      );
    } catch (err) {
      console.error('Error managing admin:', err);
      setModalError('Failed to update admin status: ' + err.message);
      setModalSuccess(null);
    }
  };

  // Handle add new user
  const handleAddUser = async () => {
    if (!newUserEmail || !newUserPassword) {
      setModalError('Please provide both email and password.');
      return;
    }
    try {
      const response = await fetch('https://us-central1-nantahala-mountain-resor-596d9.cloudfunctions.net/api/createUser', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await user.getIdToken()}`,
        },
        body: JSON.stringify({ email: newUserEmail, password: newUserPassword, isAdmin: makeAdmin }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}: ${await response.text()}`);
      }
      const result = await response.json();
      setModalSuccess(result.message);
      setModalError(null);
      // Refresh user list
      setAllUsers((prev) => [
        ...prev,
        { email: newUserEmail, uid: `new-${Date.now()}`, isAdmin: makeAdmin, preferences: { emergencyNotifications: false, updateNotifications: false } },
      ]);
      setNewUserEmail('');
      setNewUserPassword('');
      setMakeAdmin(false);
      setTimeout(() => setShowAddModal(false), 1500);
    } catch (err) {
      console.error('Error creating user:', err);
      setModalError('Failed to create user: ' + err.message);
      setModalSuccess(null);
    }
  };

  // Handle delete user
  const handleDeleteUser = async (email) => {
    if (!window.confirm(`Are you sure you want to delete user ${email}?`)) {
      return;
    }
    try {
      const response = await fetch('https://us-central1-nantahala-mountain-resor-596d9.cloudfunctions.net/api/deleteUser', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await user.getIdToken()}`,
        },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}: ${await response.text()}`);
      }
      const result = await response.json();
      setModalSuccess(result.message);
      setModalError(null);
      // Refresh user list
      setAllUsers((prev) => prev.filter((u) => u.email !== email));
    } catch (err) {
      console.error('Error deleting user:', err);
      setModalError('Failed to delete user: ' + err.message);
      setModalSuccess(null);
    }
  };

  // Handle reset user password
  const handleResetPassword = async (email) => {
    if (!window.confirm(`Are you sure you want to reset the password for ${email}?`)) {
      return;
    }
    try {
      const response = await fetch('https://us-central1-nantahala-mountain-resor-596d9.cloudfunctions.net/api/resetUserPassword', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await user.getIdToken()}`,
        },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}: ${await response.text()}`);
      }
      const result = await response.json();
      setModalSuccess(result.message);
      setModalError(null);
    } catch (err) {
      console.error('Error resetting user password:', err);
      setModalError('Failed to reset password: ' + err.message);
      setModalSuccess(null);
    }
  };

  // Handle edit preferences
  const handleEditPreferences = (userData) => {
    setEditUser(userData);
    setEditPreferences({
      emergencyNotifications: userData.preferences.emergencyNotifications,
      updateNotifications: userData.preferences.updateNotifications,
    });
    setShowEditModal(true);
  };

  // Handle save preferences
  const handleSavePreferences = async () => {
    try {
      await setDoc(doc(db, 'user_preferences', editUser.uid), {
        emergencyNotifications: editPreferences.emergencyNotifications,
        updateNotifications: editPreferences.updateNotifications,
        updatedAt: new Date(),
        updatedBy: user.uid,
      });
      setAllUsers((prev) =>
        prev.map((u) =>
          u.uid === editUser.uid
            ? { ...u, preferences: { ...editPreferences } }
            : u
        )
      );
      setModalSuccess('Preferences updated successfully');
      setModalError(null);
      setTimeout(() => setShowEditModal(false), 1500);
    } catch (err) {
      console.error('Error saving preferences:', err);
      setModalError('Failed to save preferences: ' + err.message);
      setModalSuccess(null);
    }
  };

  return (
    <div className={`admin-users-page ${isMobile ? 'admin-users-page-mobile' : ''}`}>
      <style>
        {`
          .admin-users-page {
            margin: 2rem;
            padding: 1rem;
            background: white;
            border-radius: 0.5rem;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            opacity: 0.8;
          }
          .admin-users-title {
            font-size: 1.5rem;
            margin-bottom: 1rem;
          }
          .admin-users-table {
            width: 100%;
            border-collapse: collapse;
          }
          .admin-users-table th,
          .admin-users-table td {
            border: 1px solid #ddd;
            padding: 0.5rem;
            text-align: left;
          }
          .admin-users-table th {
            background: #f3f4f6;
            font-weight: bold;
          }
          .admin-users-button {
            background: #3b82f6;
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 0.25rem;
            margin-bottom: 1rem;
          }
          .admin-users-button:hover {
            background: #2563eb;
          }
          .admin-users-action-button {
            background: #3b82f6;
            color: white;
            padding: 0.4rem 0.8rem;
            border-radius: 0.25rem;
            margin-right: 0.5rem;
          }
          .admin-users-action-button:hover {
            background: #2563eb;
          }
          .admin-users-delete-button {
            background: #dc3545;
          }
          .admin-users-delete-button:hover {
            background: #c82333;
          }
          .admin-users-reset-button {
            background: #17a2b8;
          }
          .admin-users-reset-button:hover {
            background: #138496;
          }
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
          }
          .modal-content {
            background: white;
            padding: 1.5rem;
            border-radius: 0.5rem;
            width: 90%;
            max-width: 400px;
          }
          .modal-content-mobile {
            padding: 1rem;
            max-width: 350px;
          }
          .modal-title {
            font-size: 1.25rem;
            margin-bottom: 1rem;
          }
          .modal-error {
            color: #ef4444;
            margin-bottom: 1rem;
          }
          .modal-success {
            color: #22c55e;
            margin-bottom: 1rem;
          }
          .modal-input {
            width: 100%;
            padding: 0.5rem;
            margin-bottom: 1rem;
            border: 1px solid #ddd;
            border-radius: 0.25rem;
            font-size: 1rem;
          }
          .modal-checkbox {
            display: flex;
            align-items: center;
            margin-bottom: 1rem;
          }
          .modal-checkbox input {
            margin-right: 0.5rem;
          }
          .modal-button {
            padding: 0.5rem 1rem;
            border-radius: 0.25rem;
            margin: 0.25rem;
          }
          .modal-button-primary {
            background: #3b82f6;
            color: white;
          }
          .modal-button-primary:hover {
            background: #2563eb;
          }
          .modal-button-secondary {
            background: #6c757d;
            color: white;
          }
          .modal-button-secondary:hover {
            background: #5a6268;
          }
          @media (max-width: 640px) {
            .admin-users-page {
              margin: 1rem;
              padding: 0.5rem;
            }
            .admin-users-title {
              font-size: 1.25rem;
            }
            .admin-users-table th,
            .admin-users-table td {
              padding: 0.4rem;
              font-size: 0.9rem;
            }
            .admin-users-button {
              padding: 0.4rem 0.8rem;
              font-size: 0.9rem;
            }
            .admin-users-action-button,
            .admin-users-delete-button,
            .admin-users-reset-button {
              padding: 0.3rem 0.6rem;
              font-size: 0.9rem;
              margin-right: 0.3rem;
            }
            .modal-title {
              font-size: 1rem;
            }
            .modal-input {
              font-size: 0.9rem;
              padding: 0.4rem;
            }
            .modal-checkbox {
              font-size: 0.9rem;
            }
            .modal-button {
              padding: 0.4rem 0.8rem;
              font-size: 0.9rem;
            }
          }
        `}
      </style>
      <h2 className="admin-users-title">Manage Users</h2>
      {error && <p className="text-red-500">{error}</p>}
      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <button
            onClick={() => setShowAddModal(true)}
            className="admin-users-button"
          >
            Add New User
          </button>
          {allUsers.length > 0 ? (
            <table className="admin-users-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Admin Status</th>
                  <th>Emergency Notifications</th>
                  <th>Update Notifications</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {allUsers.map((user) => (
                  <tr key={user.uid}>
                    <td>{user.email}</td>
                    <td>{user.isAdmin ? 'Active' : 'Inactive'}</td>
                    <td>{user.preferences.emergencyNotifications ? 'Enabled' : 'Disabled'}</td>
                    <td>{user.preferences.updateNotifications ? 'Enabled' : 'Disabled'}</td>
                    <td>
                      <button
                        onClick={() => handleAdminAction(user.email, !user.isAdmin, 'modify')}
                        className="admin-users-action-button"
                      >
                        {user.isAdmin ? 'Remove Admin' : 'Make Admin'}
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.email)}
                        className="admin-users-action-button admin-users-delete-button"
                      >
                        Delete User
                      </button>
                      <button
                        onClick={() => handleResetPassword(user.email)}
                        className="admin-users-action-button admin-users-reset-button"
                      >
                        Reset Password
                      </button>
                      <button
                        onClick={() => handleEditPreferences(user)}
                        className="admin-users-action-button"
                      >
                        Edit Preferences
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No users found.</p>
          )}
          {showAddModal && (
            <AddUserModal
              key="add-user-modal"
              isMobile={isMobile}
              newUserEmail={newUserEmail}
              setNewUserEmail={setNewUserEmail}
              newUserPassword={newUserPassword}
              setNewUserPassword={setNewUserPassword}
              makeAdmin={makeAdmin}
              setMakeAdmin={setMakeAdmin}
              modalError={modalError}
              modalSuccess={modalSuccess}
              handleAddUser={handleAddUser}
              setShowAddModal={setShowAddModal}
              setModalError={setModalError}
              setModalSuccess={setModalSuccess}
            />
          )}
          {showEditModal && (
            <EditPreferencesModal
              key="edit-preferences-modal"
              isMobile={isMobile}
              userData={editUser}
              editPreferences={editPreferences}
              setEditPreferences={setEditPreferences}
              modalError={modalError}
              modalSuccess={modalSuccess}
              handleSavePreferences={handleSavePreferences}
              setShowEditModal={setShowEditModal}
              setModalError={setModalError}
              setModalSuccess={setModalSuccess}
            />
          )}
        </>
      )}
    </div>
  );
}

export default AdminUsersPage;